// Social Pulse — Vision, Strategy & Product Specification.
// Long-form article linked from the Home page under the hero caption.

export const VISION_MISSION = {
  title: "Vision, Strategy & Product Specification",
  overline: "SOCIAL PULSE · VISION & MISSION",
  hero_image:
    "https://images.unsplash.com/photo-1758599669199-a858720a9689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHx2b2x1bnRlZXJzJTIwaGVscGluZyUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODQ1NzE3NzB8MA&ixlib=rb-4.1.0&q=85",
  intro:
    "Social Pulse (Social Pulse) is a global social impact platform whose mission is: \"The greatest joy in life comes from helping others.\"",
  lead:
    "Social Pulse encourages people to create a legacy during their lifetime by connecting those who want to help with those who genuinely need help — regardless of religion, nationality, race, gender, or political affiliation. This document lays out the vision, guiding values, structure, and long-term aspirations of the platform.",
  sections: [
    {
      slug: "vision",
      heading: "1. Vision",
      body: [
        "Instead of focusing only on monetary donations, Social Pulse promotes:",
      ],
      list: [
        "Acts of kindness",
        "Volunteerism",
        "Community building",
        "Social responsibility",
        "Skill sharing",
        "Sustainable poverty reduction",
      ],
      outro:
        "The platform combines technology, verified organizations, volunteers, donors, and communities into one trusted ecosystem.",
    },
    {
      slug: "mission",
      heading: "2. Mission",
      body: [
        "Create the world's largest trusted platform where every individual can:",
      ],
      list: ["Give", "Volunteer", "Mentor", "Learn", "Request help", "Build social capital", "Improve communities"],
      outro:
        "…regardless of religion, nationality, race, gender or political affiliation.",
    },
    {
      slug: "core-philosophy",
      heading: "3. Core Philosophy",
      body: [
        "People often spend years accumulating wealth. Many leave behind assets that are:",
      ],
      list: [
        "Heavily taxed",
        "Unused",
        "Donated to various religious institutions or scam 'gods'",
        "Inherited without purpose",
      ],
      outro:
        "Social Pulse encourages people to experience the joy of giving while they are alive and leave an everlasting legacy for generations. The platform believes: helping one person changes one life — helping thousands changes the world.",
    },
    {
      slug: "core-values",
      heading: "4. Core Values",
      list: [
        "Compassion",
        "Dignity",
        "Transparency",
        "Accountability",
        "Inclusion",
        "Sustainability",
        "Community",
        "Trust",
        "Respect",
        "Equality",
      ],
    },
    {
      slug: "taglines",
      heading: "5. Taglines",
      body: ["Some possibilities:"],
      list: [
        "Bringing Joy Through Helping",
        "Help Someone Today",
        "Joy Multiplies When Shared",
        "Small Acts. Big Impact.",
        "Together We Can Change Lives",
        "Helping Humanity One Person at a Time",
        "Your Kindness Creates Tomorrow",
      ],
    },
    {
      slug: "goals",
      heading: "6. Main Goals",
      groups: [
        {
          heading: "Connect",
          body: ["Connect people needing help with:"],
          list: ["Individuals", "NGOs", "Volunteers", "Companies", "Governments", "Local communities"],
        },
        { heading: "Inspire", body: ["Encourage a culture where helping becomes part of everyday life."] },
        { heading: "Verify", body: ["Build trust through verified users and organizations."] },
        { heading: "Recognize", body: ["Reward people with measurable Social Capital."] },
        { heading: "Measure", body: ["Show the real impact created globally."] },
      ],
    },
    {
      slug: "platform-structure",
      heading: "7. Platform Structure",
      body: ["The platform has four primary users."],
      groups: [
        {
          heading: "1. People Seeking Help",
          body: ["Examples:"],
          list: [
            "Medical",
            "Housing",
            "Food",
            "Education",
            "Disability assistance",
            "Transportation",
            "Elder care",
            "Funeral support",
            "Child care",
            "Emergency relief",
            "Mental health resources",
            "Legal aid",
            "Refugee support",
          ],
        },
        {
          heading: "2. Volunteers",
          body: ["People offering:"],
          list: [
            "Time",
            "Skills",
            "Knowledge",
            "Transportation",
            "Mentoring",
            "Teaching",
            "Repairs",
            "Construction",
            "Translation",
            "Technology support",
            "Companionship",
            "Disaster response",
          ],
        },
        {
          heading: "3. Organizations",
          body: ["Examples:"],
          list: [
            "NGOs",
            "Charities",
            "Community groups",
            "Food banks",
            "Shelters",
            "Schools",
            "Hospitals",
            "Churches",
            "Mosques",
            "Temples",
            "Synagogues",
            "Local governments",
            "Environmental organizations",
          ],
        },
        {
          heading: "4. Donors",
          body: ["Can contribute:"],
          list: [
            "Money",
            "Food",
            "Clothes",
            "Furniture",
            "School supplies",
            "Building materials",
            "Medical supplies",
            "Vehicles",
            "Technology",
            "Professional services",
          ],
        },
      ],
    },
    {
      slug: "categories",
      heading: "8. Categories of Help",
      groups: [
        {
          heading: "Basic Needs",
          list: ["Food", "Shelter", "Clothing", "Clean water", "Travel", "Hygiene"],
        },
        {
          heading: "Medical",
          list: [
            "Surgery",
            "Medicines",
            "Medical equipment",
            "Blood donation",
            "Organ donation",
            "Hospital transportation",
          ],
        },
        {
          heading: "Education",
          list: ["Scholarships", "School supplies", "Tuition", "Books", "Online courses", "Mentoring"],
        },
        {
          heading: "Housing",
          body: ["A major focus area. Help families:"],
          list: [
            "Build homes",
            "Repair homes",
            "Roofing",
            "Plumbing",
            "Electricity",
            "Solar panels",
            "Water systems",
            "Sanitation",
            "Travel",
          ],
        },
        {
          heading: "Employment",
          list: [
            "Resume help",
            "Job referrals",
            "Career mentoring",
            "Skill development",
            "Entrepreneurship mentoring",
            "Apprenticeships",
          ],
        },
        {
          heading: "Microfinance",
          body: [
            "Integrate with organizations like Kiva to support entrepreneurs through microloans and encourage responsible lending and repayment.",
          ],
        },
        {
          heading: "Environment",
          body: ["Community projects:"],
          list: [
            "Tree planting",
            "Walkways and trails cleaning",
            "Beach cleaning",
            "River cleaning",
            "Recycling",
            "Community gardens",
          ],
        },
        {
          heading: "Senior Citizens",
          list: [
            "Companionship",
            "Shopping assistance",
            "Transportation",
            "Shopping help",
            "Walking partners",
            "Home visits",
          ],
        },
        {
          heading: "Children",
          list: ["Tutoring", "Mentoring", "Sports", "Arts", "Nutrition"],
        },
        {
          heading: "Emergency Relief",
          list: ["Floods", "Earthquakes", "Fires", "Wars", "Displacement"],
        },
      ],
    },
    {
      slug: "verification",
      heading: "9. Verification System",
      body: ["Trust is critical. Verification levels:"],
      list: [
        "Bronze — email verified",
        "Silver — phone verified",
        "Gold — government ID verified",
        "Platinum — background checked",
      ],
      outro: "Organizations receive:",
      list_after: [
        "Registration verification",
        "Tax status verification",
        "Financial transparency rating",
        "Years of operation",
        "Impact score",
      ],
    },
    {
      slug: "social-capital",
      heading: "10. Social Capital Score",
      body: [
        "Instead of only rewarding financial donations, Social Pulse rewards impact. Users earn points for:",
      ],
      list: [
        "Volunteering",
        "Mentoring",
        "Teaching",
        "Donations",
        "Community leadership",
        "Environmental work",
        "Blood donation",
        "Organizing events",
        "Disaster response",
      ],
      groups: [
        {
          heading: "Example levels",
          list: [
            "Helper",
            "Friend",
            "Supporter",
            "Community Builder",
            "Impact Leader",
            "Joy Ambassador",
            "Global Changemaker",
          ],
        },
        {
          heading: "Benefits",
          list: [
            "Digital certificates",
            "Public profile",
            "Recruiter-friendly badge",
            "Portfolio of verified service",
            "Recognition by partner organizations",
            "Memorials with eulogies and messages",
          ],
        },
      ],
      outro:
        "Future possibilities include verified digital credentials that can be shared on professional networking sites or digital resumes.",
    },
    {
      slug: "ai-features",
      heading: "11. AI Features",
      groups: [
        {
          heading: "AI Helper",
          body: ["Suggests:"],
          list: [
            "Nearby volunteer opportunities",
            "Charities",
            "People needing help",
            "Matching skills",
            "Donation ideas",
          ],
        },
        {
          heading: "AI Matching",
          body: ["Matches:"],
          list: [
            "Volunteer → Organization",
            "Donor → Need",
            "Teacher → Student",
            "Doctor → Patient",
            "Lawyer → Family",
            "Engineer → Community",
            "Builder → Housing Project",
          ],
        },
        {
          heading: "AI Fraud Detection",
          body: ["Detects:"],
          list: [
            "Fake requests",
            "Duplicate accounts",
            "Suspicious fundraising",
            "Unusual activity",
          ],
        },
        { heading: "AI Translation", body: ["Automatic translation across languages."] },
        {
          heading: "AI Impact Reports",
          body: ["Monthly reports showing:"],
          list: [
            "Hours volunteered",
            "Lives impacted",
            "Communities helped",
            "Money donated",
            "Trees planted",
            "Homes built",
            "Education supported",
          ],
        },
      ],
    },
    {
      slug: "website-sections",
      heading: "12. Website Sections",
      groups: [
        { heading: "Home", list: ["Mission", "Stories", "Featured causes", "Statistics", "Volunteer today", "Donate"] },
        {
          heading: "Find Help",
          body: ["Search by:"],
          list: ["Country", "State", "City", "Category", "Urgency", "Distance"],
        },
        { heading: "Offer Help", list: ["Volunteer", "Donate", "Mentor", "Teach", "Sponsor"] },
        { heading: "Organizations", list: ["Verified NGOs", "Ratings", "Projects", "Needs", "Volunteers wanted"] },
        {
          heading: "Housing Projects",
          body: ["Track every home."],
          list: ["Photos", "Progress", "Cost", "Families helped", "Sponsors", "Completion"],
        },
        {
          heading: "Volunteer Marketplace",
          list: ["Organizations post opportunities", "People apply", "Schedules", "Training", "Background checks", "Certificates"],
        },
        {
          heading: "Community Projects",
          list: ["Neighborhood cleanup", "Park restoration", "Beach cleanup", "Community gardening", "Food drives"],
        },
        {
          heading: "Success Stories",
          list: ["Before and after", "Videos", "Testimonials", "Volunteer stories", "Family stories"],
        },
        {
          heading: "Dashboard",
          list: ["Personal statistics", "Achievements", "Social Capital", "Hours served", "Donations", "Projects", "Certificates"],
        },
      ],
    },
    {
      slug: "mobile",
      heading: "13. Mobile App Features",
      list: [
        "QR check-in for volunteering",
        "Live notifications",
        "Emergency requests",
        "AI assistant",
        "Digital volunteer card",
        "Maps",
        "Messaging",
        "Event calendar",
        "Volunteer hours tracker",
        "Donation receipts",
        "Offline mode for disaster response",
      ],
    },
    {
      slug: "gamification",
      heading: "14. Gamification",
      body: ["Instead of competition, encourage positive motivation."],
      groups: [
        {
          heading: "Achievements",
          list: [
            "First Volunteer",
            "100 Hours",
            "Community Hero",
            "Food Champion",
            "Education Champion",
            "Housing Hero",
            "Global Helper",
            "Earth Guardian",
            "Mentor",
            "Life Saver",
          ],
        },
        {
          heading: "Challenges",
          list: [
            "Monthly kindness challenges",
            "City leaderboards",
            "Team challenges",
            "Corporate competitions",
            "School competitions",
          ],
        },
      ],
    },
    {
      slug: "corporate",
      heading: "15. Corporate Partnerships",
      body: ["Companies can:"],
      list: [
        "Finance maintenance of website, mobile apps, databases and staff",
        "Sponsor homes",
        "Sponsor schools",
        "Volunteer employees",
        "Donate equipment",
        "Offer internships",
        "Offer jobs",
        "Support environmental projects",
      ],
      groups: [
        {
          heading: "Corporate dashboard includes",
          list: ["Volunteer hours", "ESG metrics", "Social impact", "Employee engagement"],
        },
      ],
    },
    {
      slug: "government",
      heading: "16. Government Partnerships",
      body: ["Possible collaboration:"],
      list: [
        "Public health",
        "Emergency response",
        "Disaster management",
        "Education",
        "Homeless services",
        "Employment",
        "Environmental initiatives",
      ],
    },
    {
      slug: "transparency",
      heading: "17. Transparency",
      body: [
        "Every donation tracked. Every project documented. Every organization verified. Impact visible. Financial transparency builds trust.",
      ],
    },
    {
      slug: "privacy",
      heading: "18. Privacy & Safety",
      body: ["Protect vulnerable users by:"],
      list: [
        "Minimizing the public display of personal information",
        "Requiring consent before sharing photos or stories",
        "Allowing anonymous requests where appropriate",
        "Using moderation, reporting tools, and identity verification to reduce abuse",
      ],
    },
    {
      slug: "revenue",
      heading: "19. Revenue Model (Sustainable, Not Profit-Driven)",
      body: [
        "Social Pulse can operate as a social enterprise or nonprofit while maintaining sustainable funding. Potential revenue sources:",
      ],
      list: [
        "Optional memberships with premium features for organizations",
        "Corporate sponsorships",
        "Foundation grants",
        "Individual donations to support platform operations",
        "Corporate CSR/ESG reporting services",
        "Event sponsorships",
        "API and integration services",
        "White-label solutions for large nonprofits or governments",
      ],
      outro:
        "Avoid taking commissions from aid intended for beneficiaries whenever possible, to reinforce trust.",
    },
    {
      slug: "rollout",
      heading: "20. Global Rollout",
      groups: [
        {
          heading: "Phase 1",
          list: [
            "United States",
            "Canada",
            "United Kingdom",
            "South Africa",
            "Australia",
            "New Zealand",
            "Sri Lanka",
            "Malaysia",
            "India",
            "Singapore",
          ],
        },
        { heading: "Phase 2", list: ["Europe", "South America", "Africa", "Middle East"] },
        { heading: "Phase 3", list: ["Worldwide", "Support for over 100 languages"] },
      ],
    },
    {
      slug: "integrations",
      heading: "21. Key Integrations",
      body: ["The platform should support integration with:"],
      list: [
        "Microfinance organizations such as Kiva",
        "Mapping services for nearby opportunities",
        "Payment providers for secure donations",
        "Identity verification providers",
        "Calendar services",
        "Video meeting platforms for mentoring",
        "Digital wallet support where appropriate",
        "Professional profile sharing for verified achievements",
      ],
    },
    {
      slug: "impact",
      heading: "22. Impact Metrics",
      body: ["Track meaningful outcomes rather than only platform activity. Examples include:"],
      list: [
        "Meals provided",
        "Families housed",
        "Homes repaired or built",
        "Students supported",
        "Medical treatments funded",
        "Volunteer hours",
        "Mentoring sessions",
        "Trees planted",
        "Rivers, beaches, or parks cleaned",
        "Jobs created through entrepreneurship",
        "Small businesses financed",
        "Countries and communities served",
      ],
      outro: "Display these metrics globally and by country, state/province, city, organization, and project.",
    },
    {
      slug: "future",
      heading: "23. Future Vision",
      body: [
        "Over time, Social Pulse could become the world's trusted \"social impact operating system\" — a platform where individuals, nonprofits, businesses, schools, and governments collaborate to solve real community problems. By combining verified needs, volunteers, donations, AI-powered matching, and measurable impact, Social Pulse can make helping others more accessible, transparent, and rewarding.",
      ],
    },
    {
      slug: "tech-stack",
      heading: "Suggested Technology Stack",
      body: ["To build a scalable platform suitable for both web and mobile:"],
      list: [
        "Frontend — React (website), Flutter or React Native (iOS/Android)",
        "Backend — Node.js (NestJS) or Python (FastAPI)",
        "Database — PostgreSQL with PostGIS for location-based searches",
        "Cloud — AWS, Google Cloud, or Microsoft Azure",
        "Authentication — OAuth, passkeys, and multi-factor authentication",
        "Storage — Cloud object storage for images and documents",
        "Search — Elasticsearch or OpenSearch",
        "AI — Large language models for assistance, multilingual translation, intelligent matching, and fraud detection",
        "Analytics — Real-time dashboards for impact reporting",
      ],
    },
    {
      slug: "aspiration",
      heading: "Long-Term Aspirational Goal",
      body: [
        "To inspire one billion acts of kindness and become the world's most trusted platform for connecting people who want to help with people who need help — creating lasting social impact through compassion, transparency, and measurable action.",
        "This vision emphasizes that everyone can contribute — through time, skills, knowledge, mentorship, advocacy, or financial support — and that every verified act of kindness strengthens communities and creates a more compassionate world.",
      ],
    },
    {
      slug: "food-pulse",
      heading: "Social Pulse: Connecting Communities Through Food",
      body: [
        "Social Pulse is proud to be part of the Social Pulse initiative under the Social Pulse umbrella — a growing movement dedicated to reducing food waste, improving food access, and connecting communities through technology, collaboration, and compassion.",
        "Social Pulse brings together consumers, farmers, fishermen, grocery stores, restaurants, wholesalers, food producers, charities, and volunteers on a single community-driven platform. By helping surplus food reach families instead of landfills, Social Pulse supports local economies, reduces environmental impact, and helps ensure that nutritious food reaches those who need it most. The platform promotes smarter shopping, responsible food management, and a more sustainable food ecosystem where everyone can play a role in reducing hunger and food waste.",
        "The vision behind Social Pulse comes from Chef Thomas (Tom) Vellaringattu, a chef, entrepreneur, and humanitarian based in Portland, Oregon, USA. Inspired by the belief that food has the power to bring people together and improve lives, Chef Tom founded Social Pulse to create innovative solutions that address hunger, food waste, affordability, and community well-being. His mission is simple yet powerful: to help people shop smarter, eat better, waste less, and ensure that good food reaches every table instead of every landfill.",
        "Through the partnership between Social Pulse and Social Pulse, we are building a future where communities work together to eliminate unnecessary food waste, support local food producers, strengthen food security, and ensure that everyone has access to healthy, nutritious meals.",
        "Learn more about Social Pulse at https://foodpulse.net.",
      ],
    },
  ],
};
